import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { useSmartLinkContext } from '@atlaskit/link-provider/use-smart-link-context';
import type { SmartLinkResponse } from '@atlaskit/linking-types/smart-link';

import type { AIPlugin } from '../../../aiPluginType';
import {
	getSmartLinkResolutionError,
	hasFailedSmartLinkResolution,
	isSmartLinkUnavailable,
	type SmartLinkResolutionError,
	resolveSmartLinkForAI,
} from '../../smartlink-rovo/resolve-smartlink';
import { getIsAiEntryPointDisabled } from './use-is-ai-entry-point-disabled';

export type SmartLinkAIAction = 'rovo' | 'summarize' | 'dropdown';

export type SmartLinkAIActions = {
	details?: SmartLinkResponse;
	dispatchAction: (action: SmartLinkAIAction, itemTestId?: string) => Promise<void>;
	failed: boolean;
	hidden: boolean;
	isInaccessible: boolean;
	pendingAction?: SmartLinkAIAction;
	retryResolution: () => void;
};

export type SmartLinkAIActionHandler = (
	action: SmartLinkAIAction,
	details: SmartLinkResponse,
	itemTestId?: string,
) => (() => void) | undefined;

type MetadataState = {
	details?: SmartLinkResponse;
	error?: SmartLinkResolutionError;
	failedWhenOpened: boolean;
};
type ResolutionScope = { pending: boolean };

type SmartLinkContext = ReturnType<typeof useSmartLinkContext>;
type SmartLinkStore = NonNullable<SmartLinkContext['store']>;
type SmartLinkContextResources = {
	client: SmartLinkContext['connections']['client'];
	dispatch: SmartLinkStore['dispatch'] | undefined;
	getState: SmartLinkStore['getState'] | undefined;
};
type SmartLinkEditorContext = SmartLinkContextResources & {
	api?: ExtractInjectionAPI<AIPlugin>;
	context: SmartLinkContext;
	disabled: boolean;
	latestAttempt?: symbol;
	nodeRange?: { from: number; to: number };
	smartlinkUrl: string;
};
const latestSmartLinkContextByView = new WeakMap<EditorView, SmartLinkEditorContext>();

const isSameRange = (
	left?: { from: number; to: number },
	right?: { from: number; to: number },
): boolean => left?.from === right?.from && left?.to === right?.to;

const isSameContextResources = (
	left: SmartLinkContextResources,
	right: SmartLinkContextResources,
): boolean =>
	left.client === right.client &&
	left.getState === right.getState &&
	left.dispatch === right.dispatch;

const isSameEditorContext = (
	left: SmartLinkEditorContext,
	right: SmartLinkEditorContext,
): boolean =>
	left.api === right.api &&
	left.disabled === right.disabled &&
	left.smartlinkUrl === right.smartlinkUrl &&
	isSameRange(left.nodeRange, right.nodeRange) &&
	isSameContextResources(left, right);

export const useSmartLinkAIActions = ({
	api,
	disabled,
	getActionHandler,
	nodeRange,
	smartlinkUrl,
	view,
}: {
	api?: ExtractInjectionAPI<AIPlugin>;
	disabled: boolean;
	getActionHandler: SmartLinkAIActionHandler;
	nodeRange?: { from: number; to: number };
	smartlinkUrl: string;
	view: EditorView;
}): SmartLinkAIActions => {
	const context = useSmartLinkContext();
	const [hidden] = useState(() => isSmartLinkUnavailable(context, smartlinkUrl));
	const [metadata, setMetadata] = useState<MetadataState>(() => ({
		failedWhenOpened: hasFailedSmartLinkResolution(context, smartlinkUrl),
	}));
	const [pendingAction, setPendingAction] = useState<SmartLinkAIAction>();
	const resolutionScope = useRef<ResolutionScope>();
	const actionHandler = useRef(getActionHandler);
	actionHandler.current = getActionHandler;
	const contextResources = useMemo(
		() => ({
			client: context.connections.client,
			dispatch: context.store?.dispatch,
			getState: context.store?.getState,
		}),
		[context.connections.client, context.store?.dispatch, context.store?.getState],
	);

	useLayoutEffect(() => {
		const nextContext: SmartLinkEditorContext = {
			...contextResources,
			api,
			context,
			disabled,
			nodeRange,
			smartlinkUrl,
		};
		const previousContext = latestSmartLinkContextByView.get(view);
		if (previousContext && isSameEditorContext(previousContext, nextContext)) {
			previousContext.context = context;
		} else {
			latestSmartLinkContextByView.set(view, nextContext);
		}
	}, [api, context, contextResources, disabled, nodeRange, smartlinkUrl, view]);

	const updateMetadata = useCallback(
		(scope: ResolutionScope, update: Partial<Pick<MetadataState, 'details' | 'error'>>) => {
			if (resolutionScope.current === scope) {
				setMetadata((current) => ({ ...current, ...update }));
			}
		},
		[],
	);
	const clearTransientError = useCallback(() => {
		setMetadata((current) =>
			current.error === 'transient' ? { ...current, error: undefined } : current,
		);
	}, []);
	const loadResolution = useCallback(
		async (scope: ResolutionScope) => {
			const owner = latestSmartLinkContextByView.get(view);
			if (!owner || !isSameContextResources(owner, contextResources)) {
				return;
			}
			try {
				const details = await resolveSmartLinkForAI(owner.context, smartlinkUrl);
				updateMetadata(scope, { details, error: undefined });
			} catch (error) {
				updateMetadata(scope, { error: getSmartLinkResolutionError(error) });
			}
		},
		[contextResources, smartlinkUrl, updateMetadata, view],
	);

	useLayoutEffect(() => {
		const owner = latestSmartLinkContextByView.get(view);
		if (!owner) {
			return;
		}
		const scope = { pending: false };
		resolutionScope.current = scope;
		setMetadata({ failedWhenOpened: hasFailedSmartLinkResolution(owner.context, smartlinkUrl) });
		setPendingAction(undefined);
		void loadResolution(scope);
		return () => {
			if (resolutionScope.current === scope) {
				resolutionScope.current = undefined;
			}
		};
	}, [loadResolution, smartlinkUrl, view]);

	const dispatchAction = useCallback(
		async (action: SmartLinkAIAction, itemTestId?: string) => {
			const scope = resolutionScope.current;
			const owner = latestSmartLinkContextByView.get(view);
			if (
				!scope ||
				scope.pending ||
				!owner ||
				owner.disabled ||
				getIsAiEntryPointDisabled(owner.api) ||
				view.isDestroyed
			) {
				return;
			}

			const { doc, selection } = view.state;
			const attempt = Symbol('Smart Link AI action');
			owner.latestAttempt = attempt;
			const isCurrent = () =>
				latestSmartLinkContextByView.get(view) === owner &&
				owner.latestAttempt === attempt &&
				!view.isDestroyed &&
				!getIsAiEntryPointDisabled(owner.api) &&
				view.state.doc === doc &&
				view.state.selection.eq(selection);
			scope.pending = true;
			setPendingAction(action);
			clearTransientError();
			try {
				const response = await resolveSmartLinkForAI(owner.context, smartlinkUrl);
				if (!isCurrent()) {
					return;
				}
				const currentDetails = owner.context.store?.getState()[smartlinkUrl]?.details ?? response;
				updateMetadata(scope, { details: currentDetails, error: undefined });
				if (currentDetails.meta?.access !== 'granted') {
					return;
				}
				if (action === 'summarize') {
					await owner.api?.aiDefinitions?.actions
						.prepareSmartLinkSummarizePopup?.()
						.catch(() => undefined);
				}
				if (!isCurrent()) {
					return;
				}
				const preparedDetails =
					owner.context.store?.getState()[smartlinkUrl]?.details ?? currentDetails;
				updateMetadata(scope, { details: preparedDetails });
				if (preparedDetails.meta?.access === 'granted') {
					actionHandler.current(action, preparedDetails, itemTestId)?.();
				}
			} catch (error) {
				if (isCurrent()) {
					updateMetadata(scope, { error: getSmartLinkResolutionError(error) });
				}
			} finally {
				if (owner.latestAttempt === attempt) {
					delete owner.latestAttempt;
				}
				scope.pending = false;
				if (resolutionScope.current === scope) {
					setPendingAction(undefined);
				}
			}
		},
		[clearTransientError, smartlinkUrl, updateMetadata, view],
	);

	const retryResolution = useCallback(() => {
		const scope = resolutionScope.current;
		if (scope) {
			clearTransientError();
			void loadResolution(scope);
		}
	}, [clearTransientError, loadResolution]);

	const details = metadata.details ?? context.store?.getState()[smartlinkUrl]?.details;
	return {
		details,
		dispatchAction,
		failed: metadata.error === 'transient',
		hidden,
		isInaccessible:
			(metadata.failedWhenOpened && !metadata.details) ||
			metadata.error === 'unsupported' ||
			Boolean(details && details.meta?.access !== 'granted'),
		pendingAction,
		retryResolution,
	};
};
